import React, { useEffect, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Target, TrendingUp, DollarSign, Calendar as CalIcon, Check, Flame, Sparkles, BookOpen, Trophy, Zap, ArrowUpRight, X, Plus } from 'lucide-react';
import WeeklyView from '../components/WeeklyView';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { celebrateMany } from '../lib/celebrate';

const CATEGORY_COLORS = { fixed: '#6366F1', assignment: '#EF4444', learning: '#10B981', meeting: '#F59E0B', social: '#EC4899', personal: '#8B5CF6' };
const WEEK_INITIAL = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

// Greeting by hour
const getGreeting = (t) => {
  const h = new Date().getHours();
  if (h < 12) return t('good_morning');
  if (h < 19) return t('good_afternoon');
  return t('good_evening');
};

// DailyExpenseRow — inline editable row for the dashboard "Presupuesto del día" panel
const DailyExpenseRow = ({ row, categories, theme, t, onChange, onDelete }) => {
  const [local, setLocal] = useState({ label: row.label || '', description: row.description || '', actual: row.actual || 0 });
  useEffect(() => { setLocal({ label: row.label || '', description: row.description || '', actual: row.actual || 0 }); }, [row.id, row.label, row.description, row.actual]);
  const [creatingNewCat, setCreatingNewCat] = useState(false);
  // Build a stable list of options. Always include the current row label even if it's not in `categories`.
  const options = React.useMemo(() => {
    const set = new Set(categories || []);
    if (local.label && local.label.trim()) set.add(local.label.trim());
    return Array.from(set);
  }, [categories, local.label]);
  return (
    <tr className="group" style={{ borderTop: `1px solid ${theme.border}` }}>
      <td className="p-2 align-middle">
        {creatingNewCat ? (
          <Input autoFocus value={local.label} onChange={(e) => setLocal({ ...local, label: e.target.value })}
            onBlur={() => { setCreatingNewCat(false); if (local.label !== row.label) onChange({ label: local.label }); }}
            onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
            placeholder={t('new_category')} className="text-xs h-8" style={{ background: theme.elevated, borderColor: theme.border, color: theme.textPrimary }} />
        ) : (
          // Native <select> avoids Radix portal removeChild issues during async re-renders.
          <select
            value={local.label || ''}
            onChange={(e) => {
              const v = e.target.value;
              if (v === '__new__') { setCreatingNewCat(true); return; }
              setLocal((prev) => ({ ...prev, label: v }));
              onChange({ label: v });
            }}
            className="h-8 text-sm bg-transparent border-0 outline-none w-full px-2 cursor-pointer rounded hover:bg-white/5"
            style={{ color: theme.textPrimary, colorScheme: 'dark' }}>
            {!local.label && <option value="" disabled>{t('pick_category')}</option>}
            {options.map(c => <option key={c} value={c} style={{ background: theme.card, color: theme.textPrimary }}>{c}</option>)}
            <option value="__new__" style={{ background: theme.card, color: theme.accent }}>{t('create_new')}</option>
          </select>
        )}
      </td>
      <td className="p-2 align-middle">
        <Input value={local.description} onChange={(e) => setLocal({ ...local, description: e.target.value })}
          onBlur={() => { if (local.description !== row.description) onChange({ description: local.description }); }}
          placeholder={t('desc_placeholder')} className="text-sm h-8 border-0 px-2" style={{ background: 'transparent', color: theme.textPrimary }} />
      </td>
      <td className="p-2 text-right align-middle">
        <Input type="number" inputMode="decimal" value={local.actual || ''} onChange={(e) => setLocal({ ...local, actual: e.target.value })}
          onBlur={() => { if (parseFloat(local.actual) !== parseFloat(row.actual)) onChange({ actual: parseFloat(local.actual) || 0 }); }}
          placeholder="0" className="text-sm h-8 border-0 px-2 text-right tabular-nums font-semibold" style={{ background: 'transparent', color: theme.error }} />
      </td>
      <td className="p-1 text-center">
        <button onClick={onDelete} className="opacity-0 group-hover:opacity-100 p-1" style={{ color: theme.textMuted }}>
          <X size={14} />
        </button>
      </td>
    </tr>
  );
};


// Weekly AI Summary — generates a per-week motivational summary using GPT-5.2.
// Cached server-side per ISO week. Allows re-viewing and downloading as .txt.
const WeeklyAISummary = ({ theme }) => {
  const [data, setData] = useState(null); // { summary, week_id, generated_at, stats }
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasCached, setHasCached] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data: r } = await api.get('/ai/weekly-summary');
        if (active && r?.summary) { setData(r); setHasCached(true); }
      } catch (err) { console.debug(err); }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    setLoading(true);
    try {
      const { data: r } = await api.post('/ai/weekly-summary');
      setData(r); setHasCached(true); setOpen(true);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  const download = () => {
    if (!data?.summary) return;
    const header = `Resumen semanal — ${data.week_id || ''}\nGenerado: ${new Date(data.generated_at || Date.now()).toLocaleString('es-ES')}\n\n`;
    const body = data.summary;
    const blob = new Blob([header + body], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `teko-resumen-${data.week_id || 'semanal'}.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <div className="rounded-xl overflow-hidden" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
        <div className="p-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${theme.border}` }}>
          <div className="flex items-center gap-2">
            <Sparkles size={14} style={{ color: theme.accent }} />
            <h3 className="text-sm font-medium uppercase tracking-[0.15em]" style={{ color: theme.accent }}>Resumen Semanal IA</h3>
          </div>
          {hasCached && <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ background: `${theme.success}20`, color: theme.success }}>Listo</span>}
        </div>
        <div className="p-4">
          {hasCached ? (
            <>
              <p className="text-[11px] mb-3" style={{ color: theme.textMuted }}>
                Tu reflexión personalizada de la semana está lista. Se reinicia cada lunes.
              </p>
              <div className="flex gap-2">
                <Button onClick={() => setOpen(true)} size="sm" className="flex-1 text-white" style={{ background: theme.primary }}>
                  Ver resumen
                </Button>
                <Button onClick={download} size="sm" variant="outline" style={{ borderColor: theme.border, color: theme.accent }}>
                  ↓
                </Button>
                <Button onClick={generate} size="sm" variant="outline" disabled={loading} style={{ borderColor: theme.border, color: theme.textMuted }}>
                  {loading ? '…' : '↻'}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p className="text-[11px] mb-3" style={{ color: theme.textMuted }}>
                Genera un resumen IA con tus hábitos, gastos, metas y eventos de esta semana. Lo puedes volver a ver y descargar.
              </p>
              <Button onClick={generate} disabled={loading} size="sm" className="w-full text-white" style={{ background: theme.primary }}>
                <Sparkles size={12} className="mr-1.5" />{loading ? 'Generando…' : 'Generar resumen'}
              </Button>
            </>
          )}
        </div>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl" style={{ color: theme.accent }}>
              <Sparkles size={18} /> Resumen Semanal — {data?.week_id || ''}
            </DialogTitle>
          </DialogHeader>
          <div className="prose prose-invert max-w-none whitespace-pre-wrap text-[14px] leading-relaxed"
            style={{ color: theme.textPrimary }}>
            {data?.summary || '—'}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={generate} disabled={loading} variant="outline" style={{ borderColor: theme.border, color: theme.textSecondary }}>
              {loading ? 'Generando…' : 'Regenerar'}
            </Button>
            <Button onClick={download} className="text-white" style={{ background: theme.primary }}>
              Descargar .txt
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [habits, setHabits] = useState([]);
  const [goals, setGoals] = useState([]);
  const [todayEvents, setTodayEvents] = useState([]);
  const [weeklyReview, setWeeklyReview] = useState(null);
  const [todayExpenses, setTodayExpenses] = useState([]);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [expenseCategory, setExpenseCategory] = useState('');
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseType, setExpenseType] = useState('expense');
  const [knownCategories, setKnownCategories] = useState([]);
  const [creatingNew, setCreatingNew] = useState(false);
  const [expenseDescription, setExpenseDescription] = useState('');
  const [dailyBudget, setDailyBudget] = useState(() => parseFloat(localStorage.getItem('teko-daily-budget') || '0'));
  const [showBudgetEdit, setShowBudgetEdit] = useState(false);
  const [tempBudget, setTempBudget] = useState('');
  const [tasks, setTasks] = useState([]);  // task-breakdowns for the progress card
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('teko-dashboard-view') || 'panel');
  useEffect(() => { localStorage.setItem('teko-dashboard-view', viewMode); }, [viewMode]);
  const { theme } = useTheme();
  const { user } = useAuth();
  const { t, dateLocale } = useLanguage();
  const nav = useNavigate();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      // /budget is gated by plan (Premium-only). Use Promise.allSettled so a 403 here
      // doesn't blank-out the entire dashboard for Free/Light/Pro/Trimestral users.
      const results = await Promise.allSettled([
        api.get('/dashboard'), api.get('/habits'), api.get('/goals'),
        api.get('/calendar/today'), api.get('/weekly-review'), api.get('/budget'),
        api.get('/task-breakdown'),
      ]);
      const [d, h, g, t, w, b, tb] = results.map((r) => (r.status === 'fulfilled' ? r.value : { data: null }));
      if (d.data) setData(d.data);
      if (h.data) setHabits(h.data);
      if (g.data) setGoals(g.data);
      if (t.data) setTodayEvents(t.data);
      if (w.data) setWeeklyReview(w.data);
      if (Array.isArray(tb.data)) setTasks(tb.data);

      const allBudget = Array.isArray(b.data) ? b.data : [];
      const todayStr = new Date().toISOString().split('T')[0];
      setTodayExpenses(allBudget.filter(r => r.type === 'tracker' && r.date === todayStr));
      const cats = [...new Set(allBudget.filter(r => r.label?.trim()).map(r => r.label.trim()))];
      const month = new Date().getMonth(); const year = new Date().getFullYear();
      try {
        const cb = JSON.parse(localStorage.getItem(`teko-cat-budgets-${year}-${month}`) || '{}');
        Object.keys(cb).forEach(k => { if (!cats.includes(k)) cats.push(k); });
      } catch (err) { console.debug(err); }
      setKnownCategories(cats);
    } catch (e) { console.error(e); }
  };

  const toggleEventComplete = async (id) => {
    try { await api.put(`/calendar/${id}/toggle-complete`); fetchAll(); } catch (err) { console.debug(err); }
  };

  const quickAddExpense = async () => {
    if (!expenseCategory || !expenseAmount) return;
    const today = new Date().toISOString().split('T')[0];
    try {
      await api.post('/budget', {
        category: expenseCategory,
        amount: parseFloat(expenseAmount),
        type: expenseType === 'income' ? 'income' : 'tracker',
        date: today, currency: 'USD', period: 'monthly',
        label: expenseCategory, budget: 0, actual: parseFloat(expenseAmount),
        description: expenseDescription || null,
      });
      setExpenseCategory(''); setExpenseAmount(''); setExpenseDescription(''); setCreatingNew(false); setShowExpenseDialog(false);
      fetchAll();
    } catch (err) { console.debug(err); }
  };
  const deleteExpense = async (id) => {
    try { await api.delete(`/budget/${id}`); fetchAll(); } catch (err) { console.debug(err); }
  };
  const updateTrackerRow = async (row, patch) => {
    try {
      const next = { ...row, ...patch };
      await api.put(`/budget/${row.id}`, {
        label: next.label || '', budget: 0,
        actual: parseFloat(next.actual) || 0, date: next.date, type: 'tracker',
        amount: parseFloat(next.actual) || 0,
        description: next.description || '',
      });
      fetchAll();
    } catch (err) { console.debug(err); }
  };
  const addInlineRow = async () => {
    const today = new Date().toISOString().split('T')[0];
    const defaultCat = knownCategories[0] || 'Sin categoría';
    try {
      await api.post('/budget', {
        category: defaultCat, amount: 0, type: 'tracker',
        date: today, currency: 'USD', period: 'monthly',
        label: defaultCat, budget: 0, actual: 0, description: '',
      });
      fetchAll();
    } catch (err) { console.debug(err); }
  };
  const saveDailyBudget = () => {
    const v = parseFloat(tempBudget) || 0;
    setDailyBudget(v); localStorage.setItem('teko-daily-budget', String(v)); setShowBudgetEdit(false);
  };

  if (!data && viewMode !== 'week') return <div className="text-center py-20" style={{ color: theme.textMuted }}>{t('loading')}</div>;

  const today = new Date().toISOString().split('T')[0];
  const dashboardHabits = data ? habits.filter(h => h.show_in_dashboard !== false) : [];
  const topStreak = data?.habit_streaks?.reduce((max, h) => h.streak > (max?.streak || 0) ? h : max, null);

  const getWeekDatesForHabit = () => {
    const now = new Date();
    const start = new Date(now); start.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i);
      return { str: d.toISOString().split('T')[0], day: d.getDate(), dow: d.getDay() };
    });
  };
  const weekDates = getWeekDatesForHabit();

  const getGoalProgress = (goal) => {
    if (!goal.steps?.length) return goal.completed ? 100 : 0;
    return Math.round(goal.steps.filter(s => typeof s === 'object' && s.completed).length / goal.steps.length * 100);
  };

  const activeGoals = goals.filter(g => !g.completed).slice(0, 3);
  // Show ALL today events (completed get strikethrough). Resets next day automatically because /calendar/today only returns today.
  const todayCompletionRate = todayEvents.length > 0 ? Math.round((todayEvents.filter(e => e.completed).length / todayEvents.length) * 100) : 0;
  const todayPendingCount = todayEvents.filter(e => !e.completed).length;

  const toggleHabit = async (habitId, dateStr, checked) => {
    try {
      if (checked) {
        await api.post(`/habits/${habitId}/uncheck`, { date: dateStr });
      } else {
        const { data } = await api.post(`/habits/${habitId}/check-in`, { date: dateStr });
        if (data?.new_achievements?.length) celebrateMany(data.new_achievements);
      }
      fetchAll();
    } catch (err) { console.debug(err); }
  };

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* ==== Vista toggle (Panel / Vista Semanal) ==== */}
      <div className="flex items-center justify-center sm:justify-end">
        <div className="inline-flex rounded-full p-1"
          style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          {[
            { id: 'panel',  label: 'Panel' },
            { id: 'week',   label: 'Vista Semanal' },
          ].map((v) => (
            <button key={v.id} onClick={() => setViewMode(v.id)}
              className="text-xs px-4 py-1.5 rounded-full transition-colors"
              style={{
                background: viewMode === v.id ? theme.accent : 'transparent',
                color: viewMode === v.id ? theme.bg : theme.textSecondary,
                fontWeight: viewMode === v.id ? 600 : 400,
              }}
              data-testid={`dashboard-view-${v.id}`}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {viewMode === 'week' ? <WeeklyView /> : (
      <>
      {/* ==== HERO GREETING (simplified — solo saludo with hover color shift) ==== */}
      <div className="relative overflow-hidden rounded-3xl p-8 md:p-10 group"
        style={{
          background: theme.gradient || `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
          backgroundSize: '200% 200%',
          animation: 'tekoHeroGradient 14s ease infinite',
          border: `1px solid ${theme.primary}40`,
          transition: 'background-position 1.2s ease',
        }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          e.currentTarget.style.backgroundPosition = `${x}% ${y}%`;
        }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundPosition = '50% 50%'; }}
      >
        <style>{`@keyframes tekoHeroGradient { 0%{background-position:0% 50%} 50%{background-position:100% 50%} 100%{background-position:0% 50%} }`}</style>
        <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 0%, ${theme.bg}80 100%)` }} />
        <div className="absolute -right-32 -bottom-32 w-96 h-96 rounded-full opacity-30 blur-3xl pointer-events-none transition-transform duration-700 group-hover:translate-x-8 group-hover:-translate-y-4" style={{ background: theme.accent }} />
        <div className="absolute -left-12 -top-12 w-48 h-48 rounded-full opacity-25 blur-2xl pointer-events-none transition-transform duration-700 group-hover:-translate-x-6 group-hover:translate-y-6" style={{ background: theme.primary }} />

        <div className="relative z-10 flex items-center justify-between flex-wrap gap-6">
          <div className="flex-1 min-w-[280px]">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight" style={{ color: theme.textPrimary, lineHeight: 1.1 }}>
              {getGreeting(t)}, <span className="font-medium">{user?.name?.split(' ')[0] || ''}</span>
            </h1>
            <p className="text-xs mt-3 capitalize" style={{ color: theme.textPrimary, opacity: 0.7 }}>
              <span className="font-semibold not-italic">{new Date().toLocaleDateString(dateLocale, { weekday: 'long' })}</span> · {new Date().toLocaleDateString(dateLocale, { day: 'numeric', month: 'long' })}
            </p>
          </div>
          {/* Right: today snapshot card */}
          <div className="rounded-2xl px-5 py-4 backdrop-blur-md" style={{ background: 'rgba(0,0,0,0.4)', border: '1px solid rgba(255,255,255,0.15)' }}>
            <p className="text-[9px] uppercase tracking-[0.3em] mb-2" style={{ color: theme.textPrimary, opacity: 0.55 }}>{t('your_day_glance')}</p>
            <div className="flex items-center gap-5">
              <div>
                <p className="text-3xl font-light tabular-nums" style={{ color: theme.textPrimary }}>{todayPendingCount}</p>
                <p className="text-[10px]" style={{ color: theme.textPrimary, opacity: 0.65 }}>{todayPendingCount === 1 ? t('pending') : t('pendings')}</p>
              </div>
              <div className="h-10 w-px" style={{ background: 'rgba(255,255,255,0.2)' }} />
              <div>
                <p className="text-3xl font-light tabular-nums" style={{ color: theme.textPrimary }}>{todayCompletionRate}<span className="text-base">%</span></p>
                <p className="text-[10px]" style={{ color: theme.textPrimary, opacity: 0.65 }}>{t('completed_word')}</p>
              </div>
              {topStreak && topStreak.streak > 0 && (
                <>
                  <div className="h-10 w-px" style={{ background: 'rgba(255,255,255,0.2)' }} />
                  <div className="flex items-center gap-1.5">
                    <Flame size={20} style={{ color: '#FFD580' }} />
                    <div>
                      <p className="text-3xl font-light tabular-nums" style={{ color: '#FFD580' }}>{topStreak.streak}</p>
                      <p className="text-[10px] truncate max-w-[70px]" style={{ color: theme.textPrimary, opacity: 0.65 }}>{topStreak.name}</p>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ==== Tasks progress card ==== */}
      {(() => {
        const totalSteps = tasks.reduce((s, b) => s + (b.steps?.length || 0), 0);
        const doneSteps = tasks.reduce((s, b) => s + (b.steps?.filter((x) => x.completed).length || 0), 0);
        const activeBlocks = tasks.filter((b) => {
          const total = b.steps?.length || 0;
          const done = b.steps?.filter((x) => x.completed).length || 0;
          return total === 0 || done < total;
        }).length;
        const pct = totalSteps === 0 ? 0 : Math.round((doneSteps / totalSteps) * 100);
        if (tasks.length === 0) return null;
        return (
          <div
            className="rounded-2xl p-5 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer transition-transform hover:scale-[1.005]"
            style={{ background: theme.card, border: `1px solid ${theme.border}` }}
            onClick={() => nav('/breakdown')}
            data-testid="dashboard-tasks-progress"
          >
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                style={{ background: `${theme.accent}20`, border: `1px solid ${theme.accent}50` }}>
                <Sparkles size={18} style={{ color: theme.accent }} />
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest" style={{ color: theme.textMuted }}>Tareas</p>
                <p className="text-base font-medium" style={{ color: theme.textPrimary }}>
                  {activeBlocks > 0
                    ? `${activeBlocks} en curso · ${doneSteps}/${totalSteps} pasos`
                    : '¡Todo al día!'}
                </p>
              </div>
            </div>
            <div className="flex-1 flex items-center gap-3 min-w-[160px]">
              <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: theme.elevated }}>
                <div className="h-full transition-all" style={{ width: `${pct}%`, background: theme.success }} />
              </div>
              <span className="text-sm font-medium tabular-nums shrink-0" style={{ color: theme.textPrimary }}>{pct}%</span>
            </div>
          </div>
        );
      })()}

      {/* ==== Floating Quick-Expense FAB ==== */}
      <Dialog open={showExpenseDialog} onOpenChange={(o) => { setShowExpenseDialog(o); if (!o) { setCreatingNew(false); setExpenseCategory(''); setExpenseAmount(''); setExpenseDescription(''); } }}>
        <DialogTrigger asChild>
          <button data-testid="quick-expense-btn"
            className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full flex items-center justify-center transition-transform hover:scale-110"
            style={{ background: theme.primary, color: '#fff', boxShadow: `0 10px 30px ${theme.primary}60` }}
            title="Gasto rápido">
            <DollarSign size={22} />
          </button>
        </DialogTrigger>
        <DialogContent style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <DialogHeader><DialogTitle style={{ color: theme.accent }}>{t('register_transaction')}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={expenseType} onValueChange={setExpenseType}>
              <SelectTrigger className="text-white" style={{ background: theme.elevated, borderColor: theme.border }}><SelectValue /></SelectTrigger>
              <SelectContent style={{ background: theme.card, borderColor: theme.border }}>
                <SelectItem value="expense">{t('expense')}</SelectItem>
                <SelectItem value="income">{t('income_word')}</SelectItem>
              </SelectContent>
            </Select>
            {/* Category selector */}
            <div>
              <label className="text-xs mb-1.5 block" style={{ color: theme.textMuted }}>{t('category_label')}</label>
              {knownCategories.length > 0 ? (
                <div className="flex gap-2">
                  <Select value={expenseCategory} onValueChange={(v) => setExpenseCategory(v)}>
                    <SelectTrigger data-testid="quick-expense-category" className="text-white flex-1" style={{ background: theme.elevated, borderColor: theme.border }}>
                      <SelectValue placeholder={t('pick_category')} />
                    </SelectTrigger>
                    <SelectContent style={{ background: theme.card, borderColor: theme.border }}>
                      {knownCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <p className="text-[11px] px-1" style={{ color: theme.textMuted }}>
                  Primero crea una categoría con presupuesto en Presupuesto → Presupuesto por categoría.
                </p>
              )}
            </div>
            <Input type="number" inputMode="decimal" placeholder={t('amount_label')} value={expenseAmount} onChange={(e) => setExpenseAmount(e.target.value)}
              className="text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
            <Input placeholder={`${t('description_label')} (${t('optional')})`} value={expenseDescription} onChange={(e) => setExpenseDescription(e.target.value)}
              className="text-white" style={{ background: theme.elevated, borderColor: theme.border }} />
            <Button onClick={quickAddExpense} className="w-full text-white" style={{ background: theme.primary }}>{t('register')}</Button>
            <p className="text-[10px] text-center" style={{ color: theme.textMuted }}>{t('quick_expense_hint')}</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* ==== Agenda + Hábitos en bloques separados ==== */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* AGENDA — Bloque más largo */}
        <div className="lg:col-span-7 rounded-2xl p-5" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-1 h-6 rounded-full" style={{ background: theme.primary }} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: theme.textMuted }}>{t('today')}</p>
                <h3 className="text-base font-medium" style={{ color: theme.accent }}>{t('todays_order')}</h3>
              </div>
            </div>
            <button onClick={() => nav('/calendar')} className="p-1" style={{ color: theme.textMuted }}><ArrowUpRight size={14} /></button>
          </div>
          {todayEvents.length > 0 && (
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl font-light" style={{ color: theme.textPrimary }}>{todayEvents.filter(e => e.completed).length}<span className="text-sm" style={{ color: theme.textMuted }}>/{todayEvents.length}</span></span>
              <div className="flex-1 h-1.5 rounded-full" style={{ background: theme.elevated }}>
                <div className="h-1.5 rounded-full transition-all" style={{ width: `${todayCompletionRate}%`, background: theme.gradient || theme.primary }} />
              </div>
              <span className="text-sm font-semibold" style={{ color: todayCompletionRate >= 70 ? theme.success : theme.accent }}>{todayCompletionRate}%</span>
            </div>
          )}
          <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
            {todayEvents.length > 0 ? todayEvents.slice(0, 10).map(ev => {
              const cat = ev.description?.match(/\[(\w+)\]/)?.[1] || ev.category || 'personal';
              const color = ev.color || CATEGORY_COLORS[cat] || theme.primary;
              const isComplete = ev.completed;
              return (
                <div key={ev.id} className="flex items-center gap-3 px-3 py-2 rounded-lg group transition-all"
                  style={{ background: `${color}10`, borderLeft: `3px solid ${color}`, opacity: isComplete ? 0.55 : 1 }}>
                  <button onClick={(e) => { e.stopPropagation(); toggleEventComplete(ev.id); }}
                    className="w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 transition-all"
                    style={{ borderColor: color, background: isComplete ? color : 'transparent' }}>
                    {isComplete && <Check size={9} className="text-white" strokeWidth={3} />}
                  </button>
                  <p className="text-sm flex-1 truncate"
                    style={{ color: theme.textPrimary, textDecoration: isComplete ? 'line-through' : 'none' }}>
                    {ev.title}
                  </p>
                  <span className="text-[10px]" style={{ color: theme.textMuted }}>{ev.start_time?.split('T')[1]?.substring(0, 5)}</span>
                </div>
              );
            }) : <p className="text-sm py-10 text-center" style={{ color: theme.textMuted }}>{t('no_events_today')}</p>}
          </div>
        </div>

        {/* HÁBITOS — Bloque del mismo tamaño que stat tiles, separado */}
        <div className="lg:col-span-5 rounded-2xl p-5" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-1 h-6 rounded-full" style={{ background: theme.accent }} />
              <div>
                <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: theme.textMuted }}>{t('this_week')}</p>
                <h3 className="text-base font-medium" style={{ color: theme.accent }}>{t('week_habits_title')}</h3>
              </div>
            </div>
            <button onClick={() => nav('/habits')} className="p-1" style={{ color: theme.textMuted }}><ArrowUpRight size={14} /></button>
          </div>
          {dashboardHabits.length > 0 ? (
            <>
              <div className="grid gap-1.5 mb-3" style={{ gridTemplateColumns: '1fr repeat(7, 36px)' }}>
                <div></div>
                {WEEK_INITIAL.map((d, i) => (
                  <div key={i} translate="no" lang="x-none" className="text-center text-[11px] font-semibold notranslate" style={{ color: weekDates[i].str === today ? theme.accent : theme.textMuted }}>{d}</div>
                ))}
              </div>
              <div className="space-y-2 max-h-[260px] overflow-y-auto pr-1">
                {dashboardHabits.slice(0, 6).map(habit => (
                  <div key={habit.id} className="grid gap-1.5 items-center" style={{ gridTemplateColumns: '1fr repeat(7, 36px)' }}>
                    <span className="text-[12px] truncate pr-1" style={{ color: theme.textPrimary }}>{habit.name}</span>
                    {weekDates.map(({ str, dow }) => {
                      const checked = habit.check_ins?.includes(str);
                      const isToday = str === today;
                      const activeDays = (habit.custom_days && habit.custom_days.length > 0) ? habit.custom_days.map(x => parseInt(x)) : [0, 1, 2, 3, 4, 5, 6];
                      const isActive = activeDays.includes(dow);
                      if (!isActive) {
                        return (
                          <div key={str} className="w-8 h-8 rounded-full flex items-center justify-center mx-auto" style={{ border: `1px dashed rgba(255,255,255,0.2)`, opacity: 0.3 }} title="Día inactivo">
                            <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.5)' }}>·</span>
                          </div>
                        );
                      }
                      return (
                        <button key={str} data-testid={`dash-habit-${habit.id}-${str}`}
                          onClick={() => toggleHabit(habit.id, str, checked)}
                          className="w-8 h-8 rounded-full flex items-center justify-center mx-auto transition-all hover:scale-110"
                          style={{
                            background: checked ? theme.primary : 'transparent',
                            border: `1.5px solid ${checked ? theme.primary : '#FFFFFF'}`,
                            boxShadow: checked ? `0 0 12px ${theme.primary}80` : 'none',
                          }}>
                          {checked && <Check size={14} className="text-white" strokeWidth={3} />}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-[11px] py-10 text-center" style={{ color: theme.textMuted }}>Marca hábitos con ⭐ en Hábitos</p>
          )}
        </div>
      </div>

      {/* ==== Stat tiles row ==== */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile icon={Target} label={t('goals')} value={data.total_goals - data.completed_goals} total={data.total_goals} color={theme.primary} theme={theme} onClick={() => nav('/goals')} />
        <StatTile icon={Zap} label={t('habits')} value={data.total_habits} total={null} color={theme.accent} theme={theme} onClick={() => nav('/habits')} />
        <StatTile icon={Trophy} label={t('achievements')} value={data.total_achievements} total={null} color="#F59E0B" theme={theme} onClick={() => nav('/achievements')} />
        <StatTile icon={BookOpen} label={t('journal')} value={data.journal_entries} total={null} color="#EC4899" theme={theme} onClick={() => nav('/journal')} />
      </div>

      {/* ==== PRESUPUESTO DEL DÍA (estilo planilla, vinculado a Budget) ==== */}
      {(() => {
        const spentToday = todayExpenses.reduce((s, e) => s + (parseFloat(e.actual) || 0), 0);
        const pct = dailyBudget > 0 ? Math.min(100, Math.round((spentToday / dailyBudget) * 100)) : 0;
        const over = dailyBudget > 0 && spentToday > dailyBudget;
        const remaining = dailyBudget - spentToday;
        return (
          <div className="rounded-2xl overflow-hidden" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            {/* Header with budget bar - compact */}
            <div className="px-5 pt-4 pb-3">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <h3 className="text-lg font-light tracking-tight" style={{ color: theme.textPrimary }}>{t('daily_budget')}</h3>
                  <button onClick={() => nav('/budget')} className="text-[10px] mt-0 flex items-center gap-1 opacity-70 hover:opacity-100" style={{ color: theme.accent }}>
                    {t('monthly_tracker')} <ArrowUpRight size={10} />
                  </button>
                </div>
                <div className="flex-1 min-w-[260px] max-w-[480px]">
                  {showBudgetEdit ? (
                    <div className="flex items-center gap-2">
                      <Input type="number" autoFocus placeholder="Monto del día" value={tempBudget} onChange={(e) => setTempBudget(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && saveDailyBudget()}
                        className="text-white text-sm h-9" style={{ background: theme.elevated, borderColor: theme.border }} />
                      <Button onClick={saveDailyBudget} size="sm" className="text-white h-9" style={{ background: theme.primary }}>Guardar</Button>
                      <Button onClick={() => setShowBudgetEdit(false)} size="sm" variant="outline" className="h-9" style={{ borderColor: theme.border, color: theme.textMuted }}>×</Button>
                    </div>
                  ) : dailyBudget > 0 ? (
                    <button onClick={() => { setTempBudget(String(dailyBudget)); setShowBudgetEdit(true); }} className="w-full group">
                      <div className="flex justify-between items-baseline mb-1">
                        <span className="text-xl tabular-nums font-medium" style={{ color: over ? theme.error : theme.textPrimary }}>{spentToday.toLocaleString('es-ES', { maximumFractionDigits: 0 })}</span>
                        <span className="text-xl tabular-nums" style={{ color: theme.textMuted }}>{dailyBudget.toLocaleString('es-ES', { maximumFractionDigits: 0 })}</span>
                      </div>
                      <div className="relative w-full h-3 rounded-full overflow-hidden" style={{ background: theme.elevated }}>
                        <div className="absolute inset-y-0 left-0 rounded-full transition-all"
                          style={{ width: `${Math.min(100, pct)}%`, background: over ? theme.error : theme.primary, boxShadow: over ? `0 0 12px ${theme.error}80` : `0 0 8px ${theme.primary}50` }} />
                      </div>
                      <div className="flex justify-between text-[11px] mt-1.5">
                        <span style={{ color: over ? theme.error : theme.primary }}>{pct}%</span>
                        <span style={{ color: theme.textMuted }}>100%</span>
                      </div>
                      <p className="text-right text-xs mt-1" style={{ color: over ? theme.error : theme.accent }}>
                        {over ? `Excedido: ${(spentToday - dailyBudget).toLocaleString('es-ES', { maximumFractionDigits: 0 })}` : `Restante: ${remaining.toLocaleString('es-ES', { maximumFractionDigits: 0 })}`}
                      </p>
                    </button>
                  ) : (
                    <button onClick={() => { setTempBudget(''); setShowBudgetEdit(true); }}
                      className="w-full text-sm py-2.5 rounded-lg transition-colors hover:opacity-80"
                      style={{ background: `${theme.primary}15`, color: theme.accent, border: `1px dashed ${theme.borderHover || theme.primary}40` }}>
                      {t('define_daily_budget')}
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Table */}
            <table className="w-full" style={{ tableLayout: 'fixed' }}>
              <colgroup>
                <col style={{ width: '32%' }} />
                <col />
                <col style={{ width: '140px' }} />
                <col style={{ width: '40px' }} />
              </colgroup>
              <thead>
                <tr style={{ background: theme.primary }}>
                  <th className="p-3 text-left text-sm font-bold" style={{ color: '#fff' }}>{t('category_label')}</th>
                  <th className="p-3 text-left text-sm font-bold" style={{ color: '#fff' }}>{t('description_label')}</th>
                  <th className="p-3 text-right text-sm font-bold" style={{ color: '#fff' }}>{t('amount_label')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {todayExpenses.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-6 text-sm" style={{ color: theme.textMuted }}>
                    {t('no_expenses_today')}
                  </td></tr>
                )}
                {todayExpenses.map(ex => (
                  <DailyExpenseRow key={ex.id} row={ex} categories={knownCategories} theme={theme} t={t}
                    onChange={(patch) => updateTrackerRow(ex, patch)}
                    onDelete={() => deleteExpense(ex.id)} />
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} className="p-4 text-center" style={{ borderTop: `1px solid ${theme.border}` }}>
                    <button onClick={addInlineRow}
                      className="w-12 h-12 rounded-full inline-flex items-center justify-center transition-transform hover:scale-110"
                      style={{ background: `${theme.primary}25`, border: `1.5px solid ${theme.primary}`, color: theme.accent }}>
                      <Plus size={20} />
                    </button>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        );
      })()}

      {/* ==== GOALS + WEEKLY REVIEW ==== */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Goals taking 2/3 */}
        {activeGoals.length > 0 && (
          <div className="lg:col-span-2 rounded-xl overflow-hidden" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <div className="p-4 flex items-center justify-between" style={{ borderBottom: `1px solid ${theme.border}` }}>
              <h3 className="text-sm font-medium uppercase tracking-[0.15em]" style={{ color: theme.accent }}>{t('active_goals')}</h3>
              <button onClick={() => nav('/goals')} className="text-xs flex items-center gap-1" style={{ color: theme.accent }}>
                {t('view_all')} <ArrowUpRight size={12} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {activeGoals.map(goal => {
                const pct = getGoalProgress(goal);
                const remaining = goal.end_date ? Math.max(0, Math.ceil((new Date(goal.end_date) - new Date()) / 86400000)) : null;
                return (
                  <div key={goal.id} onClick={() => nav('/goals')} className="cursor-pointer p-3 rounded-lg transition-all hover:bg-white/5" style={{ background: theme.elevated }}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <p className="text-sm font-medium flex-1" style={{ color: theme.textPrimary }}>{goal.title}</p>
                      {remaining !== null && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full shrink-0" style={{ background: remaining <= 3 ? `${theme.error}25` : `${theme.primary}25`, color: remaining <= 3 ? theme.error : theme.accent }}>
                          {remaining}d
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 h-1.5 rounded-full" style={{ background: theme.bg }}>
                        <div className="h-1.5 rounded-full transition-all" style={{ background: pct >= 100 ? theme.success : theme.primary, width: `${pct}%` }} />
                      </div>
                      <span className="text-xs font-semibold shrink-0" style={{ color: pct >= 100 ? theme.success : theme.accent }}>{pct}%</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Weekly Review */}
        {weeklyReview && (
          <div className="rounded-xl overflow-hidden" style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
            <div className="p-4" style={{ borderBottom: `1px solid ${theme.border}` }}>
              <h3 className="text-sm font-medium uppercase tracking-[0.15em]" style={{ color: theme.accent }}>{t('week_review')}</h3>
            </div>
            <div className="p-4 grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg" style={{ background: theme.elevated }}>
                <CalIcon size={14} style={{ color: theme.primary }} />
                <p className="text-lg font-light mt-1" style={{ color: theme.accent }}>{weeklyReview.completed_events}/{weeklyReview.total_events}</p>
                <p className="text-[10px]" style={{ color: theme.textMuted }}>{t('events_lbl')}</p>
              </div>
              <div className="p-3 rounded-lg" style={{ background: theme.elevated }}>
                <BookOpen size={14} style={{ color: theme.primary }} />
                <p className="text-lg font-light mt-1" style={{ color: theme.accent }}>{weeklyReview.journal_entries}</p>
                <p className="text-[10px]" style={{ color: theme.textMuted }}>{t('journal')}</p>
              </div>
              <div className="p-3 rounded-lg" style={{ background: theme.elevated }}>
                <TrendingUp size={14} style={{ color: theme.success }} />
                <p className="text-lg font-light mt-1" style={{ color: theme.success }}>${weeklyReview.week_income.toFixed(0)}</p>
                <p className="text-[10px]" style={{ color: theme.textMuted }}>{t('income')}</p>
              </div>
              <div className="p-3 rounded-lg" style={{ background: theme.elevated }}>
                <DollarSign size={14} style={{ color: theme.error }} />
                <p className="text-lg font-light mt-1" style={{ color: theme.error }}>${weeklyReview.week_expense.toFixed(0)}</p>
                <p className="text-[10px]" style={{ color: theme.textMuted }}>{t('expenses')}</p>
              </div>
            </div>
          </div>
        )}

        {/* AI Weekly Summary */}
        <WeeklyAISummary theme={theme} />
      </div>
      </>
      )}
    </div>
  );
};

const StatTile = ({ icon: Icon, label, value, total, color, theme, onClick }) => (
  <button onClick={onClick} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    className="p-5 rounded-xl text-left transition-all hover:scale-[1.02] relative overflow-hidden"
    style={{ background: theme.card, border: `1px solid ${theme.border}` }}>
    <div className="flex items-start justify-between">
      <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
        <Icon size={16} style={{ color }} />
      </div>
      <ArrowUpRight size={14} style={{ color: theme.textMuted }} />
    </div>
    <p className="text-3xl font-light mt-3" style={{ color: theme.textPrimary }}>
      {value}{total !== null && <span className="text-base" style={{ color: theme.textMuted }}>/{total}</span>}
    </p>
    <p className="text-[11px] mt-1 uppercase tracking-wider" style={{ color: theme.textMuted }}>{label}</p>
    <div className="absolute -right-6 -bottom-6 w-20 h-20 rounded-full opacity-10" style={{ background: color }} />
  </button>
);

export default Dashboard;
